<script setup>
/**
 * A radio card for pages that are one exclusive choice - login method, routing
 * mode, plan tier.
 *
 * The gated variant is the useful part: instead of hiding an option the plan
 * does not include, the card stays in place, greyed, with its upsell chip and
 * an upgrade action inside it. People can see what they would get.
 */
import SettingsStatusChip from './SettingsStatusChip.vue';

defineProps({
  value: { type: [String, Number], required: true },
  label: { type: String, required: true },
  description: { type: String, default: '' },
  icon: { type: String, default: '' },
  activeLabel: { type: String, default: '' },
  gatedLabel: { type: String, default: '' },
  disabled: { type: Boolean, default: false },
});

const modelValue = defineModel({ type: [String, Number], default: '' });
</script>

<template>
  <label
    class="flex items-start gap-3 p-4 rounded-xl outline outline-1 -outline-offset-1 min-w-0 transition-colors"
    :class="[
      disabled
        ? 'bg-n-solid-1 outline-n-weak cursor-not-allowed'
        : 'bg-n-solid-2 cursor-pointer',
      modelValue === value && !disabled
        ? 'outline-2 -outline-offset-2 outline-n-brand'
        : 'outline-n-container',
    ]"
  >
    <input
      v-model="modelValue"
      type="radio"
      :value="value"
      :disabled="disabled"
      class="mt-0.5 size-4 flex-shrink-0 accent-n-brand"
    />
    <div class="flex flex-col gap-1 min-w-0 flex-1">
      <div class="flex items-center justify-between gap-2 min-w-0">
        <span class="flex items-center gap-2 min-w-0">
          <span v-if="icon" :class="icon" class="size-4 text-n-slate-11" />
          <span
            class="text-sm font-medium truncate"
            :class="disabled ? 'text-n-slate-10' : 'text-n-slate-12'"
          >
            {{ label }}
          </span>
          <SettingsStatusChip
            v-if="activeLabel && modelValue === value"
            :label="activeLabel"
            tone="success"
          />
        </span>
        <SettingsStatusChip
          v-if="gatedLabel"
          :label="gatedLabel"
          tone="upgrade"
          icon="i-ph-lock-simple"
        />
      </div>
      <p
        v-if="description"
        class="text-sm"
        :class="disabled ? 'text-n-slate-10' : 'text-n-slate-11'"
      >
        {{ description }}
      </p>
      <!-- Upgrade action for a gated card, rendered inside it. -->
      <slot />
    </div>
  </label>
</template>
